import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDignity, useMessages, useRoom } from 'dignity.js/react';
import { createGame } from '../chess/game.js';
import { saveGame } from '../chess/gameStore.js';
import { certificateChainHead } from '../p2p/certificateStore.js';
import GameRoute from '../features/game/GameRoute.jsx';
import Leaderboard from '../features/leaderboard/Leaderboard.jsx';
import CertificateShard from '../features/network/CertificateShard.jsx';
import ArchivistCoverage from '../features/network/ArchivistCoverage.jsx';
import LobbyCertificateSync from '../features/network/LobbyCertificateSync.jsx';
import IncomingChallengeModal from '../features/play/IncomingChallengeModal.jsx';
import PlayHome from '../features/play/PlayHome.jsx';
import AccountNetwork from '../features/settings/AccountNetwork.jsx';
import RecoveryPhraseModal from '../features/settings/RecoveryPhraseModal.jsx';
import {
  isPeerIdTakenError,
  loadIdentity,
  rotateIdentityPeerSession
} from '../p2p/identity.js';
import {
  hasRecoveryBackupAcknowledged,
  markRecoveryBackupAcknowledged
} from '../p2p/recoveryBackup.js';
import {
  blockChallenger,
  loadBlockedChallengers,
  loadOpenToChallenges,
  saveOpenToChallenges,
  unblockChallenger
} from '../p2p/challengePreferences.js';
import {
  CHALLENGE_ACK_TYPE,
  CHALLENGE_DECLINE_TYPE,
  CHALLENGE_MESSAGE_TYPE,
  DEFAULT_CHALLENGE_JOIN_WAIT_MS,
  CHALLENGE_DELAY_OPTIONS,
  CHALLENGE_START_MODES,
  boardConfigFromLeaderboard,
  buildChallengeAckPayload,
  buildChallengeDeclinePayload,
  buildChallengePayload,
  challengeJoinUrl,
  deliverChallengeMessage,
  findPeerForPlayer,
  isChallengeResolved,
  markChallengeResolved,
  mergeIncomingChallenges,
  parseIncomingChallenge,
  pendingChallengeMatches,
  peerAcceptsChallenges,
  removeIncomingChallenge,
  resolveJoinWaitMs,
  resolveStartProposal
} from '../p2p/challenges.js';
import { formatDisplayName } from '../p2p/displayName.js';
import { getPlayerProfile } from '../p2p/playerProfiles.js';
import { parseGameId, parsePeerId, playerIdFromPeerId } from '../security/input.js';
import {
  allCertificateShards,
  certificateShard,
  collectLobbyGossipShards
} from '../p2p/scopes.js';
import {
  ARCHIVIST_TARGET_CEILING,
  ARCHIVIST_TARGET_FLOOR,
  archivistSettingsPayload,
  countVisibleArchivists,
  migrateArchivistSettings,
  resolveArchivistEnabled
} from '../p2p/archivistPolicy.js';
import {
  LOBBY_SCOPE,
  SIGNALING_URLS,
  createNodeConfig,
  isConstrainedClient
} from '../p2p/node.js';
import useBootstrapPeerConnection from '../p2p/useBootstrapPeerConnection.js';
import useLobbyMesh from '../p2p/useLobbyMesh.js';
import useLobbyChat from '../p2p/useLobbyChat.js';
import useStableRoomOptions from '../p2p/useStableRoomOptions.js';
import {
  LOBBY_MESH_FANOUT,
  peerIdsForPlayer,
  selectRandomMeshTargets
} from '../p2p/lobbyMesh.js';
import { loadCachedLobbyDirectory } from '../p2p/lobbyDirectoryCache.js';
import { lobbyDebug, lobbyDebugHeartbeat, lobbyDebugReport } from '../p2p/lobbyDebug.js';
import {
  publishLocalLobbyEnvelope,
  subscribeLocalLobbyEnvelope
} from '../p2p/lobbyLocalBus.js';
import {
  clearQueuedLobbyChallengeAdvert,
  publishLobbyChallengeAdvert,
  publishLobbyChatAdvert,
  publishLobbyRoomAdvert,
  queueLobbyChallengeAdvert
} from '../p2p/lobbyRendezvous.js';
import {
  challengeFromHelloOffer,
  clearPendingOutgoingChallenge,
  loadPendingOutgoingChallenge,
  savePendingOutgoingChallenge
} from '../p2p/pendingOutgoingChallenge.js';
import useLobbyGameProxy from '../p2p/useLobbyGameProxy.js';
import ReleaseNote from '../ui/ReleaseNote.jsx';
import { hostGameUrl, openGameTab } from './openGameTab.js';

import { publicUrl } from './publicUrl.js';
import { useI18n } from '../i18n/useI18n.js';
import {
  NICKNAME_KEY,
  loadOwnCountry,
  rememberPeerProfiles,
  rememberPlayerProfile,
  saveOwnCountry
} from '../p2p/playerProfiles.js';
import { playIncomingChallengeFanfare, unlockMoveSounds } from '../ui/moveSounds.js';

const ARCHIVIST_KEY = 'dechess-archivist-v1';

function initialArchivistSettings() {
  try {
    const parsed = JSON.parse(localStorage.getItem(ARCHIVIST_KEY));
    return migrateArchivistSettings(parsed, { allShards: allCertificateShards() });
  } catch {
    return migrateArchivistSettings(null);
  }
}

function persistArchivistSettings(next) {
  localStorage.setItem(ARCHIVIST_KEY, JSON.stringify(next));
  return next;
}

function initialBootstrapPeer() {
  return parsePeerId(new URLSearchParams(window.location.search).get('peer')) || '';
}

function Lobby({ identity, onIdentityChange }) {
  const { t } = useI18n();
  const [nickname, setNickname] = useState(
    () => localStorage.getItem(NICKNAME_KEY) || 'Player'
  );
  const [nicknameDraft, setNicknameDraft] = useState(nickname);
  const [country, setCountry] = useState(() => loadOwnCountry());
  const [countryDraft, setCountryDraft] = useState(country);
  const [bootstrapDraft, setBootstrapDraft] = useState(initialBootstrapPeer);
  const [bootstrapPeer, setBootstrapPeer] = useState(initialBootstrapPeer);
  const [timeControl, setTimeControl] = useState('3+0');
  const [timeClass, setTimeClass] = useState('blitz');
  const [variant, setVariant] = useState('standard');
  const [playMode, setPlayMode] = useState('standard');
  const [rated, setRated] = useState(true);
  const [activeSection, setActiveSection] = useState('play');
  const [notice, setNotice] = useState('');
  const [archivist, setArchivist] = useState(initialArchivistSettings);
  const [gossipCount, setGossipCount] = useState(0);
  const [recoveryGateOpen, setRecoveryGateOpen] = useState(false);
  const [openToChallenges, setOpenToChallenges] = useState(() =>
    loadOpenToChallenges(identity.playerId)
  );
  const [blockedChallengers, setBlockedChallengers] = useState(() =>
    loadBlockedChallengers(identity.playerId)
  );
  const [incomingChallenges, setIncomingChallenges] = useState([]);
  const blockedPlayerIds = useMemo(
    () => blockedChallengers.map((entry) => entry.playerId),
    [blockedChallengers]
  );
  const announcedChallengeIds = useRef(new Set());
  const resolvedChallengeKeys = useRef(new Set());
  const pendingChallengeAckRef = useRef(null);
  const [pendingOutgoingChallenge, setPendingOutgoingChallenge] = useState(() =>
    loadPendingOutgoingChallenge()
  );
  const pendingOutgoingRef = useRef(pendingOutgoingChallenge);
  pendingOutgoingRef.current = pendingOutgoingChallenge;
  const [pendingChallengeAction, setPendingChallengeAction] = useState(null);
  const [joinWaitMs, setJoinWaitMs] = useState(DEFAULT_CHALLENGE_JOIN_WAIT_MS);
  const [scheduledStartDraft, setScheduledStartDraft] = useState('');
  const [startMode, setStartMode] = useState(CHALLENGE_START_MODES.IMMEDIATE);
  const [startDelayMs, setStartDelayMs] = useState(CHALLENGE_DELAY_OPTIONS[0].ms);

  const nodeConfig = useMemo(() => createNodeConfig(identity), [identity]);
  const { node, status, error: nodeError } = useDignity(nodeConfig);
  const runningNode = status === 'running' ? node : null;
  const peerRotateInflightRef = useRef(false);

  // PeerJS keeps the previous id for a few seconds after hard refresh.
  useEffect(() => {
    if (!nodeError || !isPeerIdTakenError(nodeError) || peerRotateInflightRef.current) {
      return undefined;
    }
    peerRotateInflightRef.current = true;
    let cancelled = false;
    void rotateIdentityPeerSession(identity)
      .then((next) => {
        if (!cancelled) {
          onIdentityChange?.(next);
          setNotice('Peer session refreshed — rejoining the lobby…');
        }
      })
      .catch(() => undefined)
      .finally(() => {
        peerRotateInflightRef.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [identity, nodeError, onIdentityChange]);
  // After hard refresh, Online now peers remain from session cache without open
  // PeerJS links — seed discovery so chat/challenges can reopen data channels.
  const cachedBootstrapPeers = useMemo(() => (
    loadCachedLobbyDirectory({
      selfPeerId: identity.peerId,
      selfPlayerId: identity.playerId
    })
      .map((entry) => entry.peerId)
      .filter(Boolean)
      .slice(0, 16)
  ), [identity.peerId, identity.playerId]);
  useBootstrapPeerConnection(runningNode, bootstrapPeer);
  const roomOptions = useStableRoomOptions({
    bootstrapPeer,
    bootstrapPeerIds: cachedBootstrapPeers,
    metadata: {
      nickname,
      playerId: identity.playerId,
      country,
      protocolVersion: '0.3.12',
      openToChallenges,
      archivist: Boolean(archivist.enabled),
      certShard: certificateShard(identity.publicKey)
    },
    heartbeatIntervalMs: 4000,
    ttlMs: 45000
  });
  const {
    joined,
    error: roomError,
    peers,
    connectionStats
  } = useRoom(runningNode, LOBBY_SCOPE, roomOptions);
  const helloFilter = useCallback((message) => message.type === 'dechess-hello', []);
  const challengeFilter = useCallback(
    (message) =>
      message.type === CHALLENGE_MESSAGE_TYPE
      || message.type === CHALLENGE_DECLINE_TYPE
      || message.type === CHALLENGE_ACK_TYPE,
    []
  );
  const messages = useMessages(runningNode, helloFilter);
  const challengeMessages = useMessages(runningNode, challengeFilter);

  const remotePeers = useMemo(
    () => peers.filter((peer) => peer.peerId !== identity.peerId),
    [identity.peerId, peers]
  );
  const openPeerIds = connectionStats?.peerIds || [];
  useLobbyGameProxy({
    node: runningNode,
    identity,
    pendingOutgoingChallenge,
    openPeerIds
  });
  const helloConnectPeersRef = useRef([]);
  const broadcastHelloRef = useRef(async () => {});
  const pulseLobbyIdentity = useCallback((peerIds = []) => {
    if (!runningNode || !joined) {
      return;
    }
    const targets = peerIds.length ? peerIds : helloConnectPeersRef.current;
    const push = async () => {
      if (typeof runningNode.joinDiscovery === 'function') {
        await runningNode.joinDiscovery(LOBBY_SCOPE, roomOptions).catch(() => undefined);
      }
      if (typeof runningNode.announcePresence === 'function') {
        await runningNode.announcePresence(LOBBY_SCOPE).catch(() => undefined);
      }
      await broadcastHelloRef.current({
        announce: false,
        connectToPeers: targets
      });
    };
    // Brief delay so PeerJS data channels are open after ensureConnectedToPeers.
    window.setTimeout(() => {
      void push();
    }, 150);
  }, [joined, roomOptions, runningNode]);
  const ingestIncomingChallenge = useCallback((incoming) => {
    if (!openToChallenges || !incoming?.challengeId) {
      lobbyDebugReport('CHALLENGE_RECV_DROP', {
        role: 'receiver',
        reason: !openToChallenges ? 'open-to-challenges-off' : 'missing-challenge-id',
        challengeId: incoming?.challengeId || '',
        selfPeerId: identity.peerId,
        selfPlayerId: identity.playerId,
        openToChallenges
      });
      return false;
    }
    if (isChallengeResolved(resolvedChallengeKeys.current, incoming)) {
      lobbyDebugReport('CHALLENGE_RECV_DROP', {
        role: 'receiver',
        reason: 'already-resolved',
        challengeId: incoming.challengeId,
        gameId: incoming.gameId
      });
      return false;
    }
    const blocked = new Set(blockedPlayerIds.map((id) => String(id).toLowerCase()));
    if (blocked.has(String(incoming.challenger?.playerId || '').toLowerCase())) {
      lobbyDebugReport('CHALLENGE_RECV_DROP', {
        role: 'receiver',
        reason: 'blocked-challenger',
        challengeId: incoming.challengeId,
        fromPlayerId: incoming.challenger?.playerId
      });
      return false;
    }
    const profile = getPlayerProfile(incoming.challenger?.playerId);
    const enriched = {
      ...incoming,
      challenger: {
        ...incoming.challenger,
        nickname: incoming.challenger?.nickname || profile?.nickname || '',
        country: incoming.challenger?.country || profile?.country || ''
      }
    };
    const isNew = !announcedChallengeIds.current.has(enriched.challengeId)
      && !announcedChallengeIds.current.has(enriched.gameId);
    if (isNew) {
      announcedChallengeIds.current.add(enriched.challengeId);
      if (enriched.gameId) {
        announcedChallengeIds.current.add(enriched.gameId);
      }
      unlockMoveSounds();
      playIncomingChallengeFanfare();
      const challengerName = enriched.challenger.nickname
        ? formatDisplayName(enriched.challenger.nickname, enriched.challenger.playerId)
        : enriched.challenger.playerId;
      lobbyDebug('challenge-ingest', {
        challengeId: enriched.challengeId,
        from: enriched.challenger?.peerId,
        via: enriched.via || 'unknown'
      });
      lobbyDebugReport('CHALLENGE_RECV', {
        role: 'receiver',
        challengeId: enriched.challengeId,
        gameId: enriched.gameId,
        via: enriched.via || 'unknown',
        fromPeerId: enriched.challenger?.peerId,
        fromPlayerId: enriched.challenger?.playerId,
        targetPlayerId: enriched.targetPlayerId,
        selfPeerId: identity.peerId,
        selfPlayerId: identity.playerId,
        openToChallenges
      });
      setNotice(
        `${challengerName || 'A player'} challenged you. Accept or decline in chat or Account & network.`
      );
      setActiveSection('play');
      if (runningNode && joined && enriched.challenger?.peerId) {
        runningNode.broadcastMessage(
          CHALLENGE_ACK_TYPE,
          buildChallengeAckPayload({
            challengeId: enriched.challengeId,
            gameId: enriched.gameId,
            targetPlayerId: identity.playerId
          }),
          {
            broadcastScope: LOBBY_SCOPE,
            connectToPeers: [enriched.challenger.peerId],
            fanoutPeerIds: [enriched.challenger.peerId]
          }
        ).catch(() => undefined);
      }
    }
    setIncomingChallenges((current) => mergeIncomingChallenges(current, enriched, {
      resolvedKeys: resolvedChallengeKeys.current
    }));
    return isNew;
  }, [
    blockedPlayerIds,
    identity.playerId,
    joined,
    openToChallenges,
    runningNode
  ]);

  const ingestBasketChallenges = useCallback((challenges = []) => {
    (challenges || []).forEach((entry) => {
      ingestIncomingChallenge(entry);
    });
  }, [ingestIncomingChallenge]);

  // Same-origin multi-window challenge delivery (BroadcastChannel / localStorage).
  useEffect(() => {
    if (!identity?.peerId) {
      return undefined;
    }
    return subscribeLocalLobbyEnvelope((envelope) => {
      if (!envelope || envelope.senderId === identity.peerId) {
        return;
      }
      if (envelope.type !== CHALLENGE_MESSAGE_TYPE) {
        return;
      }
      const challenge = envelope.payload;
      if (!challenge?.challengeId) {
        return;
      }
      if (
        String(challenge.targetPlayerId || '').toLowerCase()
        !== String(identity.playerId || '').toLowerCase()
      ) {
        return;
      }
      lobbyDebug('challenge-local-bus', {
        challengeId: challenge.challengeId,
        from: envelope.senderId
      });
      ingestIncomingChallenge({
        ...challenge,
        via: 'local-bus'
      });
    });
  }, [identity?.peerId, identity?.playerId, ingestIncomingChallenge]);

  const [basketChats, setBasketChats] = useState([]);
  const [basketRooms, setBasketRooms] = useState([]);
  const chatSyncRef = useRef(() => ({ rooms: [], recentChat: [] }));
  const lobbyChatPendingRef = useRef(() => []);
  const lobbyRoomsPendingRef = useRef(() => []);
  const ingestBasketChats = useCallback((chats = []) => {
    if (!Array.isArray(chats) || !chats.length) {
      return;
    }
    setBasketChats((current) => {
      const keys = new Set(current.map((entry) => entry.messageKey));
      const next = [...current];
      for (const entry of chats) {
        const messageKey = entry.messageKey
          || `${entry.senderId || entry.playerId}:${entry.sentAt}:${String(entry.text || '').slice(0, 24)}`;
        if (!messageKey || keys.has(messageKey)) {
          continue;
        }
        keys.add(messageKey);
        next.push({ ...entry, messageKey });
      }
      return next.slice(-40);
    });
  }, []);
  const ingestBasketRooms = useCallback((rooms = []) => {
    if (!Array.isArray(rooms) || !rooms.length) {
      return;
    }
    setBasketRooms((current) => {
      const keys = new Set(current.map((entry) => `${entry.roomId}:${entry.createdBy}`));
      const next = [...current];
      for (const entry of rooms) {
        const key = `${entry.roomId}:${entry.createdBy || ''}`;
        if (!entry?.roomId || keys.has(key)) {
          continue;
        }
        keys.add(key);
        next.push(entry);
      }
      return next.slice(-24);
    });
  }, []);

  const {
    onlinePeers: meshPeers,
    recentPeers,
    refreshRendezvous,
    ensureLinkedToPeerIds,
    broadcastDirectory
  } = useLobbyMesh({
    node: runningNode,
    joined,
    identity,
    nickname,
    country,
    openToChallenges,
    archivist: Boolean(archivist.enabled),
    certShard: certificateShard(identity.publicKey),
    remotePeers,
    helloMessages: messages,
    bootstrapPeer,
    openPeerIds,
    onIdentityPulse: pulseLobbyIdentity,
    onBasketChallenges: ingestBasketChallenges,
    onBasketChats: ingestBasketChats,
    onBasketRooms: ingestBasketRooms,
    getChatSyncPayload: () => chatSyncRef.current()
  });
  const lobbyPeers = meshPeers.length > 0 ? meshPeers : remotePeers;
  const visibleArchivistCount = useMemo(
    () => countVisibleArchivists(lobbyPeers, { selfEnabled: Boolean(archivist.enabled) }),
    [archivist.enabled, lobbyPeers]
  );
  const constrainedClient = useMemo(() => isConstrainedClient(), []);

  // Loud periodic proof that this tab is on the new debug build.
  useEffect(() => {
    if (!joined) {
      return undefined;
    }
    const pulse = () => {
      lobbyDebugHeartbeat({
        joined: true,
        selfPeerId: identity.peerId,
        selfPlayerId: identity.playerId,
        nickname,
        openPeerIds,
        openCount: openPeerIds.length,
        onlineCount: recentPeers.length,
        onlinePeerIds: recentPeers.slice(0, 8).map((entry) => entry.peerId),
        incomingChallenges: incomingChallenges.length,
        signaling: SIGNALING_URLS[0] || ''
      });
    };
    pulse();
    const timer = window.setInterval(pulse, 8_000);
    return () => window.clearInterval(timer);
  }, [
    identity.peerId,
    identity.playerId,
    incomingChallenges.length,
    joined,
    nickname,
    openPeerIds,
    recentPeers
  ]);

  const chatMeshPeerIds = useMemo(() => {
    const preferred = [
      ...(bootstrapPeer ? [bootstrapPeer] : []),
      ...openPeerIds,
      ...cachedBootstrapPeers
    ];
    const fromDirectory = recentPeers
      .map((entry) => entry.peerId)
      .filter((peerId) => peerId && peerId !== identity.peerId);
    return [...preferred, ...fromDirectory]
      .filter((peerId, index, list) => peerId && list.indexOf(peerId) === index)
      .slice(0, 24);
  }, [
    bootstrapPeer,
    cachedBootstrapPeers,
    identity.peerId,
    openPeerIds,
    recentPeers
  ]);
  // Queue chat/room adverts; PeerBasket budget posts them on the next inbox cycle.
  // force:true used to bypass soft pace AND hammer CF into CORS/429 — do not.
  const publishChatOutOfBand = useCallback((payload) => (
    publishLobbyChatAdvert(payload, identity.peerId)
  ), [identity.peerId]);
  const publishRoomOutOfBand = useCallback((payload) => (
    publishLobbyRoomAdvert(payload, identity.peerId)
  ), [identity.peerId]);
  const onChatSent = useCallback(() => {
    // dignity.js late-join pattern: push state on every open path immediately.
    void broadcastDirectory();
    void broadcastHelloRef.current({
      announce: false,
      connectToPeers: chatMeshPeerIds
    });
    // Soft poll only — a forced basket POST after every chat message causes CF WAF.
    void refreshRendezvous({ force: false, connect: true });
  }, [broadcastDirectory, chatMeshPeerIds, refreshRendezvous]);
  const lobbyChat = useLobbyChat({
    node: runningNode,
    joined,
    identity,
    nickname,
    openPeerIds,
    preferPeerIds: chatMeshPeerIds,
    meshPeerIds: chatMeshPeerIds,
    helloMessages: messages,
    basketChats,
    basketRooms,
    ensureLinkedToPeerIds,
    publishOutOfBand: publishChatOutOfBand,
    publishRoomOutOfBand,
    onChatSent
  });
  lobbyChatPendingRef.current = lobbyChat.getPendingChatForHello;
  lobbyRoomsPendingRef.current = lobbyChat.getPendingRoomsForHello;
  chatSyncRef.current = lobbyChat.getChatSyncPayload;
  const helloConnectPeers = useMemo(() => {
    const prefer = [
      ...(bootstrapPeer ? [bootstrapPeer] : []),
      ...openPeerIds
    ];
    return selectRandomMeshTargets(recentPeers, {
      count: LOBBY_MESH_FANOUT,
      excludePeerIds: [identity.peerId],
      preferPeerIds: prefer
    });
  }, [bootstrapPeer, identity.peerId, openPeerIds, recentPeers]);
  helloConnectPeersRef.current = helloConnectPeers;
  const ownCertificateShard = useMemo(
    () => certificateShard(identity.publicKey),
    [identity.publicKey]
  );
  const peerCertShards = useMemo(() => {
    const shards = [];
    for (const peer of remotePeers) {
      if (peer.metadata?.certShard) {
        shards.push(peer.metadata.certShard);
      }
    }
    for (const peer of lobbyPeers) {
      if (peer.certShard || peer.metadata?.certShard) {
        shards.push(peer.certShard || peer.metadata.certShard);
      }
    }
    for (const message of messages) {
      if (message.payload?.certShard) {
        shards.push(message.payload.certShard);
      }
    }
    return shards;
  }, [lobbyPeers, messages, remotePeers]);
  const gossipShards = useMemo(() => {
    if (archivist.enabled) {
      return allCertificateShards();
    }
    return collectLobbyGossipShards(ownCertificateShard, peerCertShards);
  }, [archivist.enabled, ownCertificateShard, peerCertShards]);
  const certificateBootstrapPeers = useMemo(
    () => (bootstrapPeer ? [bootstrapPeer] : []),
    [bootstrapPeer]
  );
  const inviteUrl = useMemo(() => {
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    url.searchParams.set('peer', identity.peerId);
    return url.toString();
  }, [identity.peerId]);

  useEffect(() => {
    if (!joined) {
      return;
    }
    setNotice(
      bootstrapPeer
        ? t('notice.joinedVia', { scope: LOBBY_SCOPE, peer: bootstrapPeer })
        : t('notice.joined', { scope: LOBBY_SCOPE })
    );
  }, [bootstrapPeer, joined, t]);

  // Adaptive archivist: auto peers enroll under the floor and demote above the ceiling.
  useEffect(() => {
    if (archivist.preference !== 'auto') {
      return;
    }
    const nextEnabled = resolveArchivistEnabled({
      preference: 'auto',
      constrained: constrainedClient,
      visibleArchivistCount,
      previouslyAutoOn: Boolean(archivist.enabled)
    });
    if (nextEnabled === archivist.enabled) {
      return;
    }
    setArchivist(
      persistArchivistSettings(
        archivistSettingsPayload('auto', nextEnabled, allCertificateShards())
      )
    );
  }, [
    archivist.enabled,
    archivist.preference,
    constrainedClient,
    visibleArchivistCount
  ]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setNotice('');
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    rememberPlayerProfile(identity.playerId, { nickname, country });
  }, [country, identity.playerId, nickname]);

  useEffect(() => {
    rememberPeerProfiles(lobbyPeers);
  }, [lobbyPeers]);

  useEffect(() => {
    if (!openToChallenges) {
      setIncomingChallenges([]);
      return;
    }

    const blocked = new Set(blockedPlayerIds.map((id) => String(id).toLowerCase()));
    setIncomingChallenges((current) =>
      current.filter((challenge) => (
        challenge.expiresAt > Date.now()
        && !blocked.has(String(challenge.challenger?.playerId || '').toLowerCase())
      ))
    );
  }, [blockedPlayerIds, openToChallenges]);

  useEffect(() => {
    if (!openToChallenges) {
      return;
    }

    challengeMessages.forEach((message) => {
      if (message.type === CHALLENGE_ACK_TYPE) {
        const pending = pendingOutgoingRef.current;
        if (pendingChallengeMatches(pending, message.payload)) {
          pendingChallengeAckRef.current?.resolve?.(true);
          pendingChallengeAckRef.current = null;
          clearQueuedLobbyChallengeAdvert();
          // Keep pending until they join/accept path settles — board already open.
          setNotice('Opponent saw your challenge. Wait for them to join your board tab.');
        }
        return;
      }

      if (message.type === CHALLENGE_DECLINE_TYPE) {
        const pending = pendingOutgoingRef.current;
        if (pendingChallengeMatches(pending, message.payload)) {
          clearPendingOutgoingChallenge();
          clearQueuedLobbyChallengeAdvert();
          setPendingOutgoingChallenge(null);
          setNotice('Your challenge was declined.');
        }
        return;
      }

      void parseIncomingChallenge(message, {
        playerId: identity.playerId,
        openToChallenges,
        blockedPlayerIds
      }).then((incoming) => {
        if (incoming) {
          ingestIncomingChallenge({ ...incoming, via: incoming.via || 'webrtc' });
          return;
        }
        lobbyDebugReport('CHALLENGE_RECV_DROP', {
          role: 'receiver',
          reason: 'parse-rejected',
          via: 'webrtc',
          senderId: message.senderId,
          targetPlayerId: message.payload?.targetPlayerId,
          selfPlayerId: identity.playerId,
          openToChallenges,
          challengeId: message.payload?.challengeId || '',
          hint: 'WebRTC envelope arrived but parseIncomingChallenge rejected it (target mismatch, anti-spam, expired, or closed to challenges).'
        });
      });
    });
  }, [
    blockedPlayerIds,
    challengeMessages,
    identity.playerId,
    ingestIncomingChallenge,
    openToChallenges
  ]);

  // Hello offers carry pending challenges while the sender stays in the lobby.
  useEffect(() => {
    if (!openToChallenges) {
      return;
    }
    messages.forEach((message) => {
      const offer = challengeFromHelloOffer(
        message.payload?.pendingChallenge,
        message.senderId
      );
      if (!offer) {
        return;
      }
      if (String(offer.targetPlayerId).toLowerCase() !== String(identity.playerId).toLowerCase()) {
        return;
      }
      ingestIncomingChallenge(offer);
    });
  }, [identity.playerId, ingestIncomingChallenge, messages, openToChallenges]);

  function saveNickname(event) {
    event.preventDefault();
    const nextNickname = nicknameDraft.trim().slice(0, 32);
    if (!nextNickname) {
      return;
    }
    localStorage.setItem(NICKNAME_KEY, nextNickname);
    setNickname(nextNickname);
    rememberPlayerProfile(identity.playerId, { nickname: nextNickname, country });
  }

  function saveCountry(event) {
    event.preventDefault();
    const nextCountry = saveOwnCountry(countryDraft);
    setCountry(nextCountry);
    setCountryDraft(nextCountry);
    rememberPlayerProfile(identity.playerId, { nickname, country: nextCountry });
    setNotice(nextCountry ? 'Country flag saved for the ladder.' : 'Country flag cleared.');
  }

  function connectToBootstrap(event) {
    event.preventDefault();
    const peerId = parsePeerId(bootstrapDraft);
    if (!peerId || peerId === identity.peerId) {
      setNotice('Enter a different browser tab’s peer ID.');
      return;
    }
    setBootstrapPeer(peerId);
  }

  function setArchivistPreference(preference) {
    const normalized = String(preference || 'auto').toLowerCase();
    const nextEnabled = resolveArchivistEnabled({
      preference: normalized,
      constrained: constrainedClient,
      visibleArchivistCount,
      previouslyAutoOn: Boolean(archivist.enabled)
    });
    const next = persistArchivistSettings(
      archivistSettingsPayload(normalized, nextEnabled, allCertificateShards())
    );
    setArchivist(next);
    setNotice(
      next.preference === 'auto'
        ? (next.enabled
          ? `Archivist automatic — helping until ~${ARCHIVIST_TARGET_FLOOR} visible archivists.`
          : `Archivist automatic — standing by (visible ${visibleArchivistCount}, target ${ARCHIVIST_TARGET_FLOOR}–${ARCHIVIST_TARGET_CEILING}).`)
        : next.enabled
          ? 'Archivist mode on — verifying and re-serving every certificate shard.'
          : 'Archivist mode is off.'
    );
  }

  async function copyInvite() {
    await navigator.clipboard.writeText(inviteUrl);
    setNotice('Invite link copied.');
  }

  const broadcastHello = useCallback(async ({
    announce = false,
    connectToPeers
  } = {}) => {
    if (!runningNode || !joined) {
      return;
    }

    const targets = Array.isArray(connectToPeers) && connectToPeers.length > 0
      ? connectToPeers
      : helloConnectPeersRef.current;
    const pending = pendingOutgoingRef.current?.challenge || null;
    const pendingChat = typeof lobbyChatPendingRef.current === 'function'
      ? lobbyChatPendingRef.current()
      : [];
    const pendingRooms = typeof lobbyRoomsPendingRef.current === 'function'
      ? lobbyRoomsPendingRef.current()
      : [];

    try {
      await runningNode.broadcastMessage(
        'dechess-hello',
        {
          nickname,
          playerId: identity.playerId,
          country,
          openToChallenges,
          archivist: Boolean(archivist.enabled),
          certShard: certificateShard(identity.publicKey),
          sentAt: new Date().toISOString(),
          ...(pending ? { pendingChallenge: pending } : {}),
          ...(pendingChat.length ? { pendingChat } : {}),
          ...(pendingRooms.length ? { pendingRooms } : {})
        },
        {
          broadcastScope: LOBBY_SCOPE,
          connectToPeers: targets
        }
      );
      if (announce) {
        setNotice('Signed hello broadcast sent.');
      }
    } catch (sendError) {
      if (announce) {
        setNotice(`Hello failed: ${sendError.message}`);
      }
    }
  }, [
    archivist.enabled,
    country,
    identity.playerId,
    joined,
    nickname,
    openToChallenges,
    runningNode
  ]);
  broadcastHelloRef.current = broadcastHello;

  async function sendHello() {
    await broadcastHelloRef.current({ announce: true });
  }

  // Keep dignity discovery metadata in sync when the player changes name/flag.
  useEffect(() => {
    if (!runningNode || !joined || typeof runningNode.joinDiscovery !== 'function') {
      return;
    }
    void runningNode.joinDiscovery(LOBBY_SCOPE, roomOptions).catch(() => undefined);
  }, [archivist.enabled, country, joined, nickname, openToChallenges, roomOptions, runningNode]);

  useEffect(() => {
    if (!runningNode || !joined) {
      return undefined;
    }

    void broadcastHelloRef.current({ announce: false });
    const intervalId = window.setInterval(() => {
      void broadcastHelloRef.current({ announce: false });
    }, 60 * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [joined, runningNode]);

  // Re-announce once when a new open PeerJS link appears — not on every
  // random mesh fanout reshuffle (that caused a hello flood).
  const openLinksKey = [...openPeerIds].filter(Boolean).sort().join('|');
  const announcedOpenLinksRef = useRef('');
  useEffect(() => {
    if (!runningNode || !joined || !openLinksKey) {
      return;
    }
    if (announcedOpenLinksRef.current === openLinksKey) {
      return;
    }
    const previous = new Set(
      announcedOpenLinksRef.current ? announcedOpenLinksRef.current.split('|') : []
    );
    const added = openPeerIds.filter((peerId) => peerId && !previous.has(peerId));
    announcedOpenLinksRef.current = openLinksKey;
    if (!added.length && previous.size > 0) {
      return;
    }
    void broadcastHelloRef.current({
      announce: false,
      connectToPeers: added.length ? added : openPeerIds
    });
  }, [joined, openLinksKey, openPeerIds, runningNode]);

  function toggleOpenToChallenges(enabled) {
    saveOpenToChallenges(identity.playerId, enabled);
    setOpenToChallenges(enabled);
    if (!enabled) {
      setIncomingChallenges([]);
    }
    setNotice(
      enabled
        ? 'You are open to incoming ladder challenges.'
        : 'Incoming ladder challenges are turned off.'
    );
  }

  async function createChallenge(overrides = {}) {
    const nextTimeControl = overrides.timeControl ?? timeControl;
    const nextVariant = overrides.variant ?? (playMode === 'standard' ? 'standard' : variant);
    const nextRated = overrides.rated ?? rated;
    const targetPlayerId = overrides.targetPlayerId || null;
    const nextJoinWaitMs = resolveJoinWaitMs(overrides.joinWaitMs ?? joinWaitMs);
    const sentAt = Date.now();
    const startProposal = resolveStartProposal({
      mode: overrides.startMode ?? startMode,
      delayMs: overrides.startDelayMs ?? startDelayMs,
      scheduledInput: overrides.scheduledStartDraft ?? scheduledStartDraft,
      sentAt
    });
    const { scheduledStartAt, startMode: resolvedStartMode, forfeitGraceMs } = startProposal;
    const joinDeadlineAt = scheduledStartAt && scheduledStartAt > sentAt + nextJoinWaitMs
      ? scheduledStartAt + (forfeitGraceMs ?? 0)
      : sentAt + nextJoinWaitMs;

    const id = crypto.randomUUID();
    const chainHead = await certificateChainHead(identity.playerId);
    const nextGame = createGame({
      id,
      white: {
        playerId: identity.playerId,
        nickname,
        publicKey: identity.publicKey,
        chainHead
      },
      timeControl: nextTimeControl,
      variant: nextVariant,
      rated: nextRated,
      joinWaitMs: joinDeadlineAt - sentAt,
      scheduledStartAt,
      startMode: resolvedStartMode,
      forfeitGraceMs,
      challengedPlayerId: targetPlayerId || null
    });
    await saveGame(nextGame);

    if (targetPlayerId && runningNode && joined) {
      const targetPeer = findPeerForPlayer(lobbyPeers, targetPlayerId);
      const challenge = buildChallengePayload({
        gameId: id,
        challenger: {
          playerId: identity.playerId,
          nickname,
          peerId: identity.peerId,
          country
        },
        targetPlayerId,
        timeControl: nextTimeControl,
        variant: nextVariant,
        rated: nextRated,
        sentAt,
        joinWaitMs: joinDeadlineAt - sentAt,
        scheduledStartAt,
        startMode: resolvedStartMode,
        forfeitGraceMs
      });

      const targetProfile = getPlayerProfile(targetPlayerId);
      const targetLabel = targetProfile?.nickname
        ? formatDisplayName(targetProfile.nickname, targetPlayerId)
        : targetPlayerId;
      const pending = {
        challenge,
        targetPlayerId,
        targetPeerId: overrides.targetPeerId || targetPeer?.peerId || '',
        createdAt: Date.now()
      };
      // Deliver while this lobby PeerJS id is still live, then open the board
      // in the same tab so the challenge peerId stays the board node.
      savePendingOutgoingChallenge(pending);
      setPendingOutgoingChallenge(pending);
      queueLobbyChallengeAdvert(challenge);
      setActiveSection('play');
      setNotice(`Challenge sent to ${targetLabel}. Opening your board…`);

      try {
        const candidatePeerIds = [
          pending.targetPeerId,
          ...peerIdsForPlayer(recentPeers, targetPlayerId),
          ...openPeerIds.filter((peerId) => (
            String(playerIdFromPeerId(peerId) || '').toLowerCase()
            === String(targetPlayerId).toLowerCase()
          ))
        ].filter((peerId, index, list) => peerId && list.indexOf(peerId) === index);

        if (candidatePeerIds.length) {
          await ensureLinkedToPeerIds(candidatePeerIds);
        }
        publishLocalLobbyEnvelope({
          type: CHALLENGE_MESSAGE_TYPE,
          senderId: identity.peerId,
          payload: challenge
        });
        await deliverChallengeMessage(runningNode, challenge, {
          targetPeerId: candidatePeerIds[0] || '',
          candidatePeerIds,
          broadcastScope: LOBBY_SCOPE,
          preferPeerIds: candidatePeerIds,
          publishOutOfBand: (payload) => publishLobbyChallengeAdvert(payload, { force: true }),
          attempts: 4,
          settleMs: 200
        });
        await broadcastHelloRef.current({
          announce: false,
          connectToPeers: candidatePeerIds
        });
        await refreshRendezvous({ force: true, connect: true });
      } catch {
        // Board tab continues retries from the pending-outgoing record.
      }

      openGameTab(hostGameUrl(id, publicUrl));
      return;
    }

    openGameTab(hostGameUrl(id, publicUrl));
  }

  function openPendingOutgoingBoard() {
    const gameId = pendingOutgoingChallenge?.challenge?.gameId;
    if (!gameId) {
      return;
    }
    openGameTab(hostGameUrl(gameId, publicUrl));
  }

  function cancelPendingOutgoingChallenge() {
    clearPendingOutgoingChallenge();
    clearQueuedLobbyChallengeAdvert();
    setPendingOutgoingChallenge(null);
    setNotice('Outgoing challenge cancelled. You can challenge again from Online now.');
  }

  // Keep the PeerBasket challenge advert queued while we wait for ACK / accept.
  useEffect(() => {
    if (pendingOutgoingChallenge?.challenge) {
      queueLobbyChallengeAdvert(pendingOutgoingChallenge.challenge);
      return undefined;
    }
    clearQueuedLobbyChallengeAdvert();
    return undefined;
  }, [pendingOutgoingChallenge]);

  // Keep pushing the challenge while we stay in the lobby so the receiver's
  // Online now banner can populate via WebRTC, hello, or PeerBasket.
  useEffect(() => {
    if (!pendingOutgoingChallenge?.challenge || !runningNode || !joined) {
      return undefined;
    }
    const challenge = pendingOutgoingChallenge.challenge;
    const targetPlayerId = pendingOutgoingChallenge.targetPlayerId;

    const push = async () => {
      const candidatePeerIds = [
        pendingOutgoingChallenge.targetPeerId,
        ...peerIdsForPlayer(recentPeers, targetPlayerId),
        ...openPeerIds.filter((peerId) => (
          String(playerIdFromPeerId(peerId) || '').toLowerCase()
          === String(targetPlayerId).toLowerCase()
        ))
      ].filter((peerId, index, list) => peerId && list.indexOf(peerId) === index);

      if (candidatePeerIds.length) {
        await ensureLinkedToPeerIds(candidatePeerIds);
      }
      publishLocalLobbyEnvelope({
        type: CHALLENGE_MESSAGE_TYPE,
        senderId: identity.peerId,
        payload: challenge
      });
      await deliverChallengeMessage(runningNode, challenge, {
        targetPeerId: candidatePeerIds[0] || '',
        candidatePeerIds,
        broadcastScope: LOBBY_SCOPE,
        preferPeerIds: candidatePeerIds,
        publishOutOfBand: (payload) => publishLobbyChallengeAdvert(payload, { force: true }),
        attempts: 2,
        settleMs: 150
      });
      await broadcastHelloRef.current({
        announce: false,
        connectToPeers: candidatePeerIds.length ? candidatePeerIds : undefined
      });
      // Force a rendezvous cycle so the queued challenge advert is (re)posted
      // and the receiver's next force poll can see fresh peer ids + inbox.
      await refreshRendezvous({ force: true, connect: true });
    };

    void push();
    const timer = window.setInterval(() => {
      void push();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [
    ensureLinkedToPeerIds,
    joined,
    openPeerIds,
    pendingOutgoingChallenge,
    recentPeers,
    refreshRendezvous,
    runningNode
  ]);

  function requestCreateChallenge(overrides = {}) {
    if (hasRecoveryBackupAcknowledged(identity.playerId)) {
      createChallenge(overrides);
      return;
    }
    setPendingChallengeAction({ type: 'create', overrides });
    setRecoveryGateOpen(true);
  }

  function requestChallengeLeaderboardPlayer(targetPlayerId, board) {
    if (targetPlayerId === identity.playerId) {
      return;
    }
    if (!joined || !runningNode) {
      setNotice('Join the lobby before sending a challenge.');
      return;
    }

    const config = boardConfigFromLeaderboard(board);
    const targetPeer = findPeerForPlayer(lobbyPeers, targetPlayerId);
    if (targetPeer && !peerAcceptsChallenges(targetPeer)) {
      const targetProfile = getPlayerProfile(targetPlayerId);
      const targetLabel = targetProfile?.nickname
        ? formatDisplayName(targetProfile.nickname, targetPlayerId)
        : targetPlayerId;
      setNotice(`${targetLabel} is not accepting ladder challenges right now.`);
      return;
    }

    setPlayMode(config.playMode);
    setVariant(config.variant);
    setTimeClass(config.timeClass);
    setTimeControl(config.timeControl);
    setRated(config.rated);
    setActiveSection('play');

    requestCreateChallenge({
      ...config,
      targetPlayerId,
      joinWaitMs,
      scheduledStartDraft,
      startMode,
      startDelayMs
    });
  }

  function requestChallengeOnlinePlayer(targetPlayerId, options = {}) {
    if (targetPlayerId === identity.playerId) {
      return;
    }
    if (!joined || !runningNode) {
      setNotice('Join the lobby before sending a challenge.');
      return;
    }

    const targetPeer = findPeerForPlayer(lobbyPeers, targetPlayerId);
    const targetPeerId = options.peerId || targetPeer?.peerId || null;
    if (targetPeer && !peerAcceptsChallenges(targetPeer)) {
      const targetProfile = getPlayerProfile(targetPlayerId);
      const targetLabel = targetProfile?.nickname
        ? formatDisplayName(targetProfile.nickname, targetPlayerId)
        : targetPlayerId;
      setNotice(`${targetLabel} is not accepting ladder challenges right now.`);
      return;
    }

    requestCreateChallenge({
      targetPlayerId,
      targetPeerId,
      timeControl: options.timeControl || timeControl,
      variant: options.variant
        || (playMode === 'standard' ? 'standard' : variant),
      rated: typeof options.rated === 'boolean' ? options.rated : rated,
      joinWaitMs,
      scheduledStartDraft,
      startMode,
      startDelayMs
    });
  }

  function openChallengePreferences() {
    setActiveSection('account');
    window.setTimeout(() => {
      const target = document.getElementById('incoming-challenges')
        || document.getElementById('challenge-preferences');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  function blockAllIncomingChallenges() {
    toggleOpenToChallenges(false);
    openChallengePreferences();
  }

  async function blockIncomingChallenger(challenge) {
    const challengerId = challenge?.challenger?.playerId;
    if (!challengerId) {
      return;
    }
    const next = blockChallenger(identity.playerId, challengerId, {
      nickname: challenge.challenger?.nickname || ''
    });
    setBlockedChallengers(next);
    setNotice(
      `${formatDisplayName(
        challenge.challenger?.nickname || 'Player',
        challengerId
      )} blocked. Manage the list in Account & network.`
    );
    await declineIncomingChallenge(challenge);
    openChallengePreferences();
  }

  function unblockBlockedChallenger(playerId) {
    const next = unblockChallenger(identity.playerId, playerId);
    setBlockedChallengers(next);
    setNotice('Player unblocked for ladder challenges.');
  }

  function confirmRecoveryAndCreateChallenge() {
    markRecoveryBackupAcknowledged(identity.playerId);
    setRecoveryGateOpen(false);
    if (pendingChallengeAction?.type === 'create') {
      createChallenge(pendingChallengeAction.overrides || {});
      setPendingChallengeAction(null);
      return;
    }
    createChallenge();
  }

  function acceptIncomingChallenge(challenge) {
    markChallengeResolved(resolvedChallengeKeys.current, challenge);
    setIncomingChallenges((current) => removeIncomingChallenge(current, challenge));
    setNotice('Challenge accepted. Opening the board…');
    openGameTab(challengeJoinUrl(challenge));
  }

  async function declineIncomingChallenge(challenge) {
    markChallengeResolved(resolvedChallengeKeys.current, challenge);
    setIncomingChallenges((current) => removeIncomingChallenge(current, challenge));
    if (!runningNode || !joined) {
      return;
    }

    try {
      await runningNode.broadcastMessage(
        CHALLENGE_DECLINE_TYPE,
        buildChallengeDeclinePayload({
          challengeId: challenge.challengeId,
          gameId: challenge.gameId,
          targetPlayerId: identity.playerId,
          declinedBy: identity.playerId
        }),
        {
          broadcastScope: LOBBY_SCOPE,
          connectToPeers: [challenge.challenger.peerId]
        }
      );
      setNotice('Challenge declined.');
    } catch (sendError) {
      setNotice(`Challenge declined locally: ${sendError.message}`);
    }
  }

  const effectiveError = nodeError || roomError;

  return (
    <main id="main-content" className="app-shell" tabIndex="-1">
      <a className="skip-link" href="#main-content">{t('nav.skip')}</a>
      <header className="site-header">
        <button
          type="button"
          className="brand brand-button"
          onClick={() => setActiveSection('play')}
          aria-label={t('nav.home')}
        >
          <img className="brand-mark" src={publicUrl('logo.png')} alt="" width="52" height="52" />
          <img className="brand-wordmark" src={publicUrl('wordmark.png')} alt="deChess" height="30" />
        </button>

        <nav className="main-nav" aria-label={t('nav.main')}>
          <button
            type="button"
            className={activeSection === 'play' ? 'active' : ''}
            onClick={() => setActiveSection('play')}
          >
            {t('nav.play')}
          </button>
          <button
            type="button"
            className={activeSection === 'leaderboard' ? 'active' : ''}
            onClick={() => setActiveSection('leaderboard')}
          >
            {t('nav.leaderboard')}
          </button>
          <button
            type="button"
            className={activeSection === 'account' ? 'active' : ''}
            onClick={() => setActiveSection('account')}
          >
            {t('nav.account')}
          </button>
        </nav>

        <div className="header-end">
          {activeSection === 'play' && (
            <button
              type="button"
              className="text-button header-challenge-pref"
              onClick={openChallengePreferences}
            >
              {t('nav.blockAll')}
            </button>
          )}
          <button
            type="button"
            className="profile-chip"
            onClick={() => setActiveSection('account')}
          >
            <img className="avatar avatar-logo" src={publicUrl('logo.png')} alt="" width="36" height="36" />
            <span>{formatDisplayName(nickname, identity.playerId)}</span>
            <i className={joined ? 'online' : ''} aria-label={joined ? t('nav.online') : t('nav.connecting')} />
          </button>
        </div>
      </header>

      {effectiveError && (
        <div className="error" role="alert">{effectiveError.message}</div>
      )}

      {runningNode && (
        <LobbyCertificateSync
          node={runningNode}
          identity={identity}
          joined={joined && !effectiveError}
          connectPeerIds={openPeerIds}
          archivist={archivist.enabled}
          onStored={() => setGossipCount((count) => count + 1)}
        />
      )}

      {runningNode && !archivist.enabled && gossipShards.map((shard) => (
        <CertificateShard
          key={shard}
          node={runningNode}
          scope={shard}
          identity={identity}
          archivist={false}
          bootstrapPeerIds={certificateBootstrapPeers}
          onStored={() => setGossipCount((count) => count + 1)}
        />
      ))}

      {runningNode && (
        <ArchivistCoverage
          node={runningNode}
          identity={identity}
          enabled={archivist.enabled}
          ownCertificateShard={ownCertificateShard}
          peerShards={peerCertShards}
          bootstrapPeerIds={certificateBootstrapPeers}
          onStored={() => setGossipCount((count) => count + 1)}
        />
      )}

      {notice && (activeSection === 'play' || activeSection === 'account') && (
        <div className="play-notice-toast" role="status" aria-live="polite">
          {notice}
        </div>
      )}

      {activeSection === 'play' ? (
        <PlayHome
          nickname={nickname}
          playerId={identity.playerId}
          joined={joined && !effectiveError}
          remotePeers={lobbyPeers}
          helloMessages={messages}
          inviteUrl={inviteUrl}
          onCopyInvite={copyInvite}
          onChallengePlayer={requestChallengeOnlinePlayer}
          timeControl={timeControl}
          setTimeControl={setTimeControl}
          timeClass={timeClass}
          setTimeClass={setTimeClass}
          playMode={playMode}
          setPlayMode={setPlayMode}
          variant={variant}
          setVariant={setVariant}
          rated={rated}
          setRated={setRated}
          createChallenge={() => requestCreateChallenge()}
          openNetworkSettings={() => setActiveSection('account')}
          incomingChallenges={incomingChallenges}
          onAcceptChallenge={acceptIncomingChallenge}
          onDeclineChallenge={declineIncomingChallenge}
          pendingOutgoingChallenge={pendingOutgoingChallenge}
          onOpenPendingOutgoingBoard={openPendingOutgoingBoard}
          onCancelPendingOutgoingChallenge={cancelPendingOutgoingChallenge}
          openToChallenges={openToChallenges}
          onToggleOpenToChallenges={toggleOpenToChallenges}
          joinWaitMs={joinWaitMs}
          setJoinWaitMs={setJoinWaitMs}
          scheduledStartDraft={scheduledStartDraft}
          setScheduledStartDraft={setScheduledStartDraft}
          startMode={startMode}
          setStartMode={setStartMode}
          startDelayMs={startDelayMs}
          setStartDelayMs={setStartDelayMs}
          lobbyChat={lobbyChat}
          onContinueMatch={(url) => openGameTab(url)}
        />
      ) : activeSection === 'leaderboard' ? (
        <Leaderboard
          identity={identity}
          revision={gossipCount}
          joined={joined && !effectiveError}
          subscribedShards={gossipShards}
          onlinePlayerIds={[
            ...(joined && !effectiveError ? [identity.playerId] : []),
            ...lobbyPeers.map((peer) => peer.playerId || peer.metadata?.playerId).filter(Boolean)
          ]}
          onChallengePlayer={requestChallengeLeaderboardPlayer}
        />
      ) : (
        <AccountNetwork
          identity={identity}
          nickname={nickname}
          nicknameDraft={nicknameDraft}
          setNicknameDraft={setNicknameDraft}
          saveNickname={saveNickname}
          country={country}
          countryDraft={countryDraft}
          setCountryDraft={setCountryDraft}
          saveCountry={saveCountry}
          inviteUrl={inviteUrl}
          copyInvite={copyInvite}
          bootstrapDraft={bootstrapDraft}
          setBootstrapDraft={setBootstrapDraft}
          connectToBootstrap={connectToBootstrap}
          remotePeers={remotePeers}
          connectionStats={connectionStats}
          sendHello={sendHello}
          joined={joined}
          messages={messages}
          scope={LOBBY_SCOPE}
          signalingCount={SIGNALING_URLS.length}
          notice={notice || status}
          ownCertificateShard={ownCertificateShard}
          archivist={archivist}
          setArchivistPreference={setArchivistPreference}
          visibleArchivistCount={visibleArchivistCount}
          archivistFloor={ARCHIVIST_TARGET_FLOOR}
          archivistCeiling={ARCHIVIST_TARGET_CEILING}
          constrainedClient={constrainedClient}
          gossipCount={gossipCount}
          openToChallenges={openToChallenges}
          onToggleOpenToChallenges={toggleOpenToChallenges}
          blockedChallengers={blockedChallengers}
          onUnblockChallenger={unblockBlockedChallenger}
          incomingChallenges={openToChallenges ? incomingChallenges : []}
          onAcceptChallenge={acceptIncomingChallenge}
          onDeclineChallenge={declineIncomingChallenge}
          pendingOutgoingChallenge={pendingOutgoingChallenge}
          onOpenPendingOutgoingBoard={openPendingOutgoingBoard}
          onCancelPendingOutgoingChallenge={cancelPendingOutgoingChallenge}
        />
      )}

      <IncomingChallengeModal
        challenges={
          openToChallenges && activeSection !== 'account'
            ? incomingChallenges
            : []
        }
        onAccept={acceptIncomingChallenge}
        onDecline={declineIncomingChallenge}
        onBlockPlayer={blockIncomingChallenger}
        onBlockAllChallenges={blockAllIncomingChallenges}
        onReviewInAccount={openChallengePreferences}
      />

      <RecoveryPhraseModal
        open={recoveryGateOpen}
        onConfirm={confirmRecoveryAndCreateChallenge}
      />

      <footer className="site-footer">
        <ReleaseNote />
      </footer>
    </main>
  );
}

export default function App() {
  const { t } = useI18n();
  const [identity, setIdentity] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadIdentity().then(setIdentity).catch(setError);
  }, []);

  if (error) {
    return <div className="boot-message error">{t('boot.failed', { message: error.message })}</div>;
  }

  if (!identity) {
    return <div className="boot-message">{t('boot.creating')}</div>;
  }

  const gameId = parseGameId(new URLSearchParams(window.location.search).get('game'));
  if (gameId) {
    return (
      <GameRoute
        identity={identity}
        gameId={gameId}
        onIdentityChange={setIdentity}
      />
    );
  }

  return (
    <Lobby
      identity={identity}
      onIdentityChange={setIdentity}
    />
  );
}
